import { useEffect, useState } from 'react';
import { qd, apiError } from '../api';

export default function QdAccounts() {
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState('users');
  const [form, setForm] = useState(null);
  const [notice, setNotice] = useState('');

  const load = () => {
    qd.get('/users').then((r) => setUsers(r.data));
    qd.get('/activity').then((r) => setActivity(r.data));
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    setNotice('');
    try {
      const { data } = await qd.post('/users', form);
      setNotice(data.email_sent
        ? `User created — login email with temporary password sent to ${data.user.email}.`
        : `User created but the email failed (${data.email_error}). Temporary password: ${data.temp_password} — share it securely.`);
      setForm(null); load();
    } catch (e) { setNotice(apiError(e)); }
  };

  const patch = async (uid, updates) => {
    await qd.patch(`/users/${uid}`, updates); load();
  };

  const resetPw = async (uid) => {
    const { data } = await qd.post(`/users/${uid}/reset-password`);
    setNotice(`New temporary password: ${data.temp_password}`);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    setNotice('');
    try {
      await qd.delete(`/users/${u.id}`);
      setNotice(`User ${u.email} deleted.`);
      load();
    } catch (e) { setNotice(apiError(e)); }
  };

  const [sending, setSending] = useState(false);

  const emailLogin = async (u) => {
    if (sending) return;
    if (!window.confirm(`Send a login email with a NEW temporary password to ${u.email}? Their current password will be replaced.`)) return;
    setNotice('');
    setSending(true);
    try {
      await qd.post(`/users/${u.id}/send-credentials`);
      setNotice(`Login email with a new temporary password sent to ${u.email}.`);
    } catch (e) { setNotice(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center gap-4 mb-6">
        <h1 className="text-xl font-bold text-[#14233F]">Accounts</h1>
        <button onClick={() => setTab('users')} className={`text-sm ${tab === 'users' ? 'font-bold text-[#0F7D70]' : 'text-slate-500'}`} data-testid="qd-tab-users">Users</button>
        <button onClick={() => setTab('activity')} className={`text-sm ${tab === 'activity' ? 'font-bold text-[#0F7D70]' : 'text-slate-500'}`} data-testid="qd-tab-activity">Activity log</button>
        {tab === 'users' && (
          <button onClick={() => setForm({ email: '', name: '', role: 'user' })} className="ml-auto bg-[#0F7D70] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="qd-new-user-btn">Invite user</button>
        )}
      </div>

      {notice && <p className="mb-4 text-sm bg-teal-50 border border-teal-200 text-teal-800 rounded px-4 py-2.5 mono" data-testid="qd-accounts-notice">{notice}</p>}

      {form && (
        <div className="bg-white border border-slate-200 rounded p-5 mb-6 flex flex-wrap gap-3 items-center" data-testid="qd-user-form">
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="border border-slate-300 rounded px-3 py-2 text-sm w-64" data-testid="qd-user-email" />
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" className="border border-slate-300 rounded px-3 py-2 text-sm w-48" data-testid="qd-user-name" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="qd-user-role">
            <option value="user">User</option><option value="admin">Admin</option><option value="superadmin">Superadmin</option>
          </select>
          <button onClick={createUser} className="bg-[#14233F] text-white px-4 py-2 rounded text-sm" data-testid="qd-user-create">Create</button>
          <button onClick={() => setForm(null)} className="border border-slate-300 px-4 py-2 rounded text-sm text-slate-600">Cancel</button>
        </div>
      )}

      {tab === 'users' ? (
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Edit products</th>
                <th className="text-left px-4 py-3">Last login</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-800">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600">{u.name}</td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={(e) => patch(u.id, { role: e.target.value })} className="border border-slate-200 rounded px-1.5 py-1 text-xs" data-testid={`qd-role-${u.email}`}>
                      <option value="user">user</option><option value="admin">admin</option><option value="superadmin">superadmin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${u.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === 'user' ? (
                      <button onClick={() => patch(u.id, { can_edit_products: !u.can_edit_products })}
                        role="switch" aria-checked={!!u.can_edit_products}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.can_edit_products ? 'bg-[#0F7D70]' : 'bg-slate-300'}`}
                        title={u.can_edit_products ? 'Can edit products — click to revoke' : 'Cannot edit products — click to allow'}
                        data-testid={`qd-editproducts-${u.email}`}>
                        <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${u.can_edit_products ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-400">always</span>
                    )}
                  </td>

                  <td className="px-4 py-3 mono text-xs text-slate-400">{u.last_login?.slice(0, 16).replace('T', ' ') || '—'}</td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    <button onClick={() => patch(u.id, { status: u.status === 'active' ? 'suspended' : 'active' })}
                      className="text-xs text-amber-600 hover:underline" data-testid={`qd-suspend-${u.email}`}>
                      {u.status === 'active' ? 'Suspend' : 'Reactivate'}
                    </button>
                    <button onClick={() => resetPw(u.id)} className="text-xs text-[#0F7D70] hover:underline" data-testid={`qd-resetpw-${u.email}`}>Reset password</button>
                    <button onClick={() => emailLogin(u)} disabled={sending} className="text-xs text-blue-600 hover:underline disabled:opacity-40" data-testid={`qd-emaillogin-${u.email}`}>{sending ? 'Sending…' : 'Email login'}</button>
                    <button onClick={() => deleteUser(u)} className="text-xs text-red-600 hover:underline" data-testid={`qd-delete-${u.email}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Who</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {activity.map((a) => (
                <tr key={a.id} className="border-b border-slate-100">
                  <td className="px-4 py-2.5 mono text-xs text-slate-400">{a.at?.slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{a.user_email}</td>
                  <td className="px-4 py-2.5 mono text-xs font-semibold text-[#14233F]">{a.action}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
